import React, { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BOOKS, AI_BOOKS } from "../../data/content";

const heroReveal = {
  hidden: { y: "110%" },
  show: (i = 0) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.12, duration: 1.1, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

/**
 * Cinematic hero cover: 3-stage motion
 *  1) Entrance from below with slight rotation (spring-eased)
 *  2) Continuous idle float (yoyo with unique phase/duration per book)
 *  3) Hover: magnify + deeper shadow + reveal series micro-label
 */
function HeroBook({
  src,
  alt,
  series,
  className = "",
  floatRange = 10,
  floatDuration = 6,
  baseRotate = 0,
  enterDelay = 0,
  testId,
  parallaxY,
  mouseTilt = false,
}) {
  return (
    <motion.div
      className={`absolute ${className} group`}
      style={{
        y: parallaxY,
        transformOrigin: "50% 60%",
        transform: mouseTilt ? "translate3d(var(--mx,0), var(--my,0), 0)" : undefined,
      }}
      initial={{ opacity: 0, y: 90, rotate: baseRotate + 12 }}
      animate={{ opacity: 1, y: 0, rotate: baseRotate }}
      transition={{
        delay: enterDelay,
        duration: 1.3,
        ease: [0.16, 1, 0.3, 1],
      }}
      whileHover={{ scale: 1.06, rotate: baseRotate * 0.4, zIndex: 20 }}
      data-testid={testId}
    >
      {/* Idle floating loop wrapper */}
      <motion.div
        animate={{ y: [-floatRange / 2, floatRange / 2, -floatRange / 2] }}
        transition={{
          duration: floatDuration,
          repeat: Infinity,
          ease: "easeInOut",
          delay: enterDelay,
        }}
      >
        <img
          src={src}
          alt={alt}
          className="w-full rounded-[6px] block"
          style={{
            filter: "drop-shadow(0 24px 34px rgba(28,25,23,0.28))",
            transition: "filter 400ms ease",
          }}
        />
        {/* Series micro-label on hover */}
        <motion.span
          initial={{ opacity: 0, y: 6 }}
          whileHover={{ opacity: 1, y: 0 }}
          animate={{ opacity: 0, y: 6 }}
          className="pointer-events-none absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[color:var(--ink)] text-[color:var(--paper)] text-[9px] tracking-[0.28em] uppercase px-2.5 py-1 whitespace-nowrap"
          style={{ transition: "none" }}
        >
          {series}
        </motion.span>
      </motion.div>
    </motion.div>
  );
}

export default function Hero() {
  const wrapRef = useRef(null);
  const mouseX = useRef(0);
  const mouseY = useRef(0);

  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 800], [0, -80]);
  const y2 = useTransform(scrollY, [0, 800], [0, -160]);
  const y3 = useTransform(scrollY, [0, 800], [0, -40]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const handle = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      mouseX.current = (e.clientX - cx) / rect.width;
      mouseY.current = (e.clientY - cy) / rect.height;
      el.style.setProperty("--mx", `${mouseX.current * 18}px`);
      el.style.setProperty("--my", `${mouseY.current * 18}px`);
      el.style.setProperty("--rx", `${-mouseY.current * 4}deg`);
      el.style.setProperty("--ry", `${mouseX.current * 6}deg`);
    };
    window.addEventListener("mousemove", handle);
    return () => window.removeEventListener("mousemove", handle);
  }, []);

  return (
    <section
      id="top"
      ref={wrapRef}
      className="relative min-h-[100svh] pt-28 md:pt-32 pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Top hairline meta bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-center text-[11px] uppercase tracking-[0.3em] text-stone-500"
      >
        <span className="hidden md:inline">AI-crafted picture books · Est. 2026</span>
      </motion.div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-12 md:mt-16 grid grid-cols-12 gap-6 md:gap-8 items-center">
        {/* Left: kinetic type */}
        <div className="col-span-12 md:col-span-8 relative z-10">
          <h1
            className="font-serif text-[16vw] md:text-[10.5vw] leading-[0.85] tracking-tight text-[color:var(--ink)]"
            data-testid="hero-title"
          >
            <span className="reveal-mask">
              <motion.span variants={heroReveal} initial="hidden" animate="show" custom={0}>
                Books for
              </motion.span>
            </span>
            <span className="reveal-mask">
              <motion.span
                variants={heroReveal}
                initial="hidden"
                animate="show"
                custom={1}
                className="italic"
                style={{ color: "var(--accent-orange)" }}
              >
                little hands.
              </motion.span>
            </span>
          </h1>

          <div className="mt-8 md:mt-10 grid grid-cols-12 gap-6">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
              className="col-span-12 md:col-span-6 text-[17px] md:text-[19px] leading-relaxed text-stone-700"
              data-testid="hero-subtitle"
            >
              A quiet, curious series of children's picture books building early <em className="font-serif not-italic italic">financial literacy</em> and <em className="font-serif not-italic italic">AI literacy</em> — story on one side, hands-on activities on the other. Meet <em className="font-serif not-italic italic">Maya</em> across two series: <em className="font-serif italic">Finance for Little Hands</em> and <em className="font-serif italic">AI for Little Hands.</em>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.35, duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
              className="col-span-12 md:col-span-6 flex flex-wrap gap-3 md:justify-end items-start md:items-center"
            >
              <a href="#books" className="btn-solid" data-testid="hero-cta-explore">
                Explore the books
                <span>→</span>
              </a>
              <a href="#newsletter" className="btn-outline" data-testid="hero-cta-newsletter">
                Newsletter
              </a>
            </motion.div>
          </div>
        </div>

        {/* Right: cinematic book display — 5 covers from both series */}
        <div className="col-span-12 md:col-span-4 relative h-[58vh] md:h-[72vh]">
          {/* Ambient breathing halo */}
          <motion.div
            aria-hidden
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: [0.4, 0.7, 0.4], scale: [0.9, 1.02, 0.9] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(60% 55% at 50% 50%, rgba(249,115,22,0.20), transparent 70%)",
              filter: "blur(20px)",
            }}
          />

          {/* Slow-rotating dashed ring */}
          <motion.svg
            aria-hidden
            viewBox="0 0 200 200"
            className="absolute inset-0 w-full h-full pointer-events-none"
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
          >
            <circle
              cx="100"
              cy="100"
              r="80"
              fill="none"
              stroke="rgba(28,25,23,0.18)"
              strokeWidth="0.4"
              strokeDasharray="1.5 2.5"
            />
          </motion.svg>

          {/* Books — all 6 covers, 3 from each series */}
          <HeroBook
            src={BOOKS[0].cover}
            alt={BOOKS[0].title}
            series="Finance"
            className="top-[-2%] left-[-4%] w-[46%]"
            floatRange={10}
            floatDuration={6.4}
            baseRotate={-4}
            enterDelay={0.5}
            testId="hero-book-1"
            parallaxY={y1}
          />
          <HeroBook
            src={AI_BOOKS[0].cover}
            alt={AI_BOOKS[0].title}
            series="AI"
            className="top-[1%] right-[-2%] w-[44%]"
            floatRange={9}
            floatDuration={5.4}
            baseRotate={5}
            enterDelay={0.6}
            testId="hero-book-ai-1"
            parallaxY={y3}
            mouseTilt
          />
          <HeroBook
            src={BOOKS[1].cover}
            alt={BOOKS[1].title}
            series="Finance"
            className="top-[30%] left-[6%] w-[48%]"
            floatRange={12}
            floatDuration={7}
            baseRotate={-2}
            enterDelay={0.7}
            testId="hero-book-2"
            parallaxY={y2}
          />
          <HeroBook
            src={AI_BOOKS[1].cover}
            alt={AI_BOOKS[1].title}
            series="AI"
            className="top-[34%] right-[2%] w-[46%]"
            floatRange={11}
            floatDuration={6.2}
            baseRotate={3}
            enterDelay={0.8}
            testId="hero-book-ai-2"
            parallaxY={y3}
          />
          <HeroBook
            src={BOOKS[2].cover}
            alt={BOOKS[2].title}
            series="Finance"
            className="bottom-[1%] left-[-3%] w-[48%]"
            floatRange={13}
            floatDuration={6.6}
            baseRotate={4}
            enterDelay={0.9}
            testId="hero-book-3"
            parallaxY={y2}
          />
          <HeroBook
            src={AI_BOOKS[2].cover}
            alt={AI_BOOKS[2].title}
            series="AI"
            className="bottom-[3%] right-[-2%] w-[46%]"
            floatRange={10}
            floatDuration={5.8}
            baseRotate={-3}
            enterDelay={1.0}
            testId="hero-book-ai-3"
            parallaxY={y1}
          />

          {/* Small legend badge */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.8, duration: 0.8 }}
            className="absolute bottom-2 right-0 md:right-4 bg-[color:var(--paper)] border border-stone-900/15 px-3 py-2 text-[10px] tracking-[0.28em] uppercase text-stone-600 flex items-center gap-2 shadow-[0_8px_30px_rgba(28,25,23,0.10)]"
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: "var(--accent-orange)" }}
            />
            06 Books · 02 Series
          </motion.div>
        </div>
      </div>

      {/* Bottom stats bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="mt-16 md:mt-24 max-w-[1400px] mx-auto px-6 md:px-10 hairline pt-6 flex flex-wrap gap-8 text-[12px] uppercase tracking-[0.28em] text-stone-600"
        data-testid="hero-stats"
      >
        <span>06 Books available now</span>
        <span className="hidden md:inline">·</span>
        <span data-testid="hero-finance-kindle-note">Finance series · Kindle eBooks now available</span>
        <span className="hidden md:inline">·</span>
        <span>02 Series</span>
        <span className="hidden md:inline">·</span>
        <span>Ages 5+</span>
        <span className="ml-auto">Scroll ↓</span>
      </motion.div>
    </section>
  );
}
